import { useMemo, useState } from "react";
import { stockLines } from "@lagerlog/core";
import type { State, StockLine } from "@lagerlog/core";
import { Containers, containerWords, euro, pricesShown } from "../catalog.ts";
import type { Article, Catalog } from "../catalog.ts";
import { useCatalog, useDrinks, useLedger, useStore } from "../hooks.ts";
import { SubNav } from "./SubNav.tsx";
import { useWide } from "../wide.ts";
import { fmt } from "./Book.tsx";
import { IS_PANTRY, WORDS, compareLagers } from "../brand.ts";

const date = (at: number) => new Date(at).toLocaleDateString("de-DE");

/** Quantity, one part per line ("3 Fl. +" / "200 ml"); bottles, never cartons (B1). */
export function Parts({ a, qty }: { a: Article | undefined; qty: number }) {
  return <Lines parts={fmt(a, qty).split(" + ")} />;
}

/** Parts of a quantity or a sum, one per line ("120 Fl. +" / "3 Fässer"). */
export function Lines({ parts }: { parts: string[] }) {
  return (
    <>
      {parts.map((part, i, all) => (
        <span key={i}>{i < all.length - 1 ? `${part} +` : part}</span>
      ))}
    </>
  );
}

/**
 * Stock and value per storage and article; a tap shows locations and lots. On a wide screen the
 * chosen wine shows on the right as a table of its lots with date, price and value.
 */
export function Stock() {
  const { store } = useStore();
  const catalog = useCatalog();
  const drinks = useDrinks();
  const ledger = useLedger();
  const wide = useWide();
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState("");

  const groups = useMemo(() => {
    if (!ledger) return [];
    const byLager = new Map<string, Map<string, StockLine[]>>();
    for (const line of stockLines(ledger)) {
      const articles = byLager.get(line.lager) ?? new Map<string, StockLine[]>();
      const lines = articles.get(line.article) ?? [];
      lines.push(line);
      articles.set(line.article, lines);
      byLager.set(line.lager, articles);
    }
    const order = compareLagers((id) => catalog?.lagers.get(id)?.name ?? id);
    return [...byLager.entries()].sort(([a], [b]) => order(a, b));
  }, [ledger, catalog]);

  if (!catalog || !ledger) return null;
  const q = filter.toLowerCase();
  const shown = groups.filter(([lager]) => store.config?.lagers.includes(lager));
  // Staff and viewers see quantities only (F20).
  // VorratsBuch keeps values off unless the household records prices (C4; docs/18-vorratsbuch.md, Profile).
  const prices = pricesShown(catalog, store.prices);

  const list = (
    <div className="stock">
      <SubNav current="bestand" />
      <input placeholder="Filtern" value={filter} onChange={(e) => setFilter(e.target.value)} />
      {shown.map(([lager, articles]) => {
        const rows = [...articles.entries()]
          .map(([id, lines]) => ({ id, lines, article: catalog.articles.get(id), qty: lines.reduce((s, l) => s + l.qty, 0), value: lines.reduce((s, l) => s + l.value, 0) }))
          .filter((r) => !q || (r.article?.name ?? r.id).toLowerCase().includes(q))
          .sort((a, b) => (a.article?.name ?? a.id).localeCompare(b.article?.name ?? b.id));
        const total = rows.reduce((s, r) => s + r.value, 0);
        return (
          <section key={lager}>
            <h2 className="row between">
              <span>{catalog.lagers.get(lager)?.name ?? lager}</span>
              {prices && <span className="muted">{euro(total)}</span>}
            </h2>
            <ul className="list">
              {rows.map((r) => {
                const key = `${lager}|${r.id}`;
                return (
                  <li key={r.id} className={wide && open === key ? "selected" : ""}>
                    <button className={`row between ${r.qty < 0 ? "negative" : ""}`} onClick={() => setOpen(open === key && !wide ? null : key)}>
                      <span>{r.article?.name ?? r.id}</span>
                      <span className="side">
                        <Parts a={r.article} qty={r.qty} />
                        {prices && <span className="muted">{euro(r.value)}</span>}
                      </span>
                    </button>
                    {!wide && open === key && (
                      <ul className="detail">
                        {r.lines.map((l) => (
                          <li key={`${l.location}|${l.lot}`} className="row between">
                            <span>
                              {catalog.locations.get(l.location)?.name ?? l.location}
                              {l.lot && <span className="muted"> · Posten {ledger.lots.get(l.lot)?.receivedAt ? date(ledger.lots.get(l.lot)!.receivedAt) : l.lot.slice(0, 8)}</span>}
                            </span>
                            <span>
                              {fmt(r.article, l.qty)} {prices && l.unpriced && <span className="muted">(ohne Preis)</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );

  if (!wide) return list;

  const [lager, article] = open?.split("|") ?? [];
  const lines = lager && article ? groups.find(([l]) => l === lager)?.[1].get(article) : undefined;
  return (
    <div className="split">
      <div className="pane">{list}</div>
      <div className="pane side-pane">
        {lines && article ? (
          <Lots catalog={catalog} ledger={ledger} article={catalog.articles.get(article)} lager={lager!} lines={lines} prices={prices} />
        ) : (
          <Totals catalog={catalog} groups={shown} prices={prices} />
        )}
      </div>
    </div>
  );
}

/** Where a wine lies in a storage and from which delivery, with price and value. */
function Lots({ catalog, ledger, article, lager, lines, prices }: { catalog: Catalog; ledger: State; article: Article | undefined; lager: string; lines: StockLine[]; prices: boolean }) {
  const sorted = [...lines].sort((a, b) => (catalog.locations.get(a.location)?.name ?? "").localeCompare(catalog.locations.get(b.location)?.name ?? "") || (ledger.lots.get(a.lot)?.receivedAt ?? 0) - (ledger.lots.get(b.lot)?.receivedAt ?? 0));
  const qty = lines.reduce((s, l) => s + l.qty, 0);
  const value = lines.reduce((s, l) => s + l.value, 0);
  return (
    <div className="card">
      <h2>{article?.name}</h2>
      <p className="muted">{catalog.lagers.get(lager)?.name}</p>
      <table className="grid">
        <thead>
          <tr>
            <th>Lagerort</th>
            <th>Posten vom</th>
            <th>Menge</th>
            {prices && <th className="num">je {article ? containerWords(article).one : WORDS.unit}</th>}
            {prices && <th className="num">Wert</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map((l) => {
            const lot = ledger.lots.get(l.lot);
            return (
              <tr key={`${l.location}|${l.lot}`} className={l.qty < 0 ? "negative" : ""}>
                <td>{catalog.locations.get(l.location)?.name ?? l.location}</td>
                <td>{lot ? date(lot.receivedAt) : l.qty < 0 ? "Fehlbestand" : "–"}</td>
                <td className="stack-cell">
                  <Parts a={article} qty={l.qty} />
                </td>
                {prices && <td className="num">{lot && lot.unitPrice !== null && article ? euro(lot.unitPrice * article.bottle) : <span className="muted">ohne Preis</span>}</td>}
                {prices && <td className="num">{euro(l.value)}</td>}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={2}>Summe</th>
            <th className="stack-cell">
              <Parts a={article} qty={qty} />
            </th>
            {prices && <th />}
            {prices && <th className="num">{euro(value)}</th>}
          </tr>
        </tfoot>
      </table>
      <p className="hint" data-testid="lot-order">Zuerst geht, was zuerst abläuft; ohne MHD der älteste Posten.</p>
    </div>
  );
}

/** Before a wine is chosen: each storage with wines, bottles and value. */
function Totals({ catalog, groups, prices }: { catalog: Catalog; groups: [string, Map<string, StockLine[]>][]; prices: boolean }) {
  const drinks = useDrinks();
  const rows = groups.map(([lager, articles]) => {
    const lines = [...articles.values()].flat();
    const bottles = new Containers();
    for (const l of lines) bottles.add(catalog.articles.get(l.article), l.qty);
    return { lager, wines: articles.size, bottles, value: lines.reduce((s, l) => s + l.value, 0) };
  });
  // Kegs and cans apart from bottles; the column says „Flaschen“ only when there are no others.
  const sum = new Containers();
  for (const [, articles] of groups) for (const l of [...articles.values()].flat()) sum.add(catalog.articles.get(l.article), l.qty);
  const parts = (c: Containers) => (sum.mixed ? <Lines parts={c.parts()} /> : c.parts()[0]!.replace(" Fl.", ""));
  return (
    <div className="card">
      <h2>{prices ? "Lagerwert" : "Bestand"}</h2>
      <table className="grid">
        <thead>
          <tr>
            <th>Lager</th>
            <th className="num">{drinks ? "Getränke" : WORDS.many}</th>
            {!IS_PANTRY && <th className="num">{sum.mixed ? "Gebinde" : "Flaschen"}</th>}
            {prices && <th className="num">Wert</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.lager}>
              <td>{catalog.lagers.get(r.lager)?.name ?? r.lager}</td>
              <td className="num">{r.wines}</td>
              {!IS_PANTRY && <td className="num stack-cell">{parts(r.bottles)}</td>}
              {prices && <td className="num">{euro(r.value)}</td>}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th>Summe</th>
            <th className="num">{rows.reduce((s, r) => s + r.wines, 0)}</th>
            {!IS_PANTRY && <th className="num stack-cell">{parts(sum)}</th>}
            {prices && <th className="num">{euro(rows.reduce((s, r) => s + r.value, 0))}</th>}
          </tr>
        </tfoot>
      </table>
      <p className="hint">Links {drinks ? "ein Getränk" : WORDS.aOne} wählen: Hier stehen dann die Posten mit Lieferdatum{prices ? ", Preis und Wert" : ""}.</p>
    </div>
  );
}
